import { Button, CircularProgress } from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useMemo, useState } from "react";
import TaskModal from "views/admin/Tasks/TaskModal";
import { P } from "const/permissions";
import { hasPermission } from "utils/permissionUtils";
import { useModuleEntryTasks } from "utils/taskService";
import ModuleTaskCard from "./ModuleTaskCard";

const isLiveTask = (task) => task?.status !== "COMPLETED" && task?.status !== "CANCELLED";

/**
 * Tasks tab for module entry pages (admission/RPL/visa/insurance/skill). Lists the tasks
 * linked to this entry with full detail, status changes, subtasks and discussion; creating
 * here links the task back automatically. The open-count badge on the tab itself comes from
 * the detail payload (openTaskCount), so opening the page costs no extra call.
 */
const ModuleTasksTab = ({
  entryType,
  entryId,
  clientId,
  clientName,
  taskModule,
  assignTitle,
  onTasksChanged,
}) => {
  const [assignOpen, setAssignOpen] = useState(false);
  const canAssignTask = hasPermission(P.TASKS.CREATE);

  const { data: tasks = [], isFetching } = useModuleEntryTasks(entryType, entryId);
  const openCount = useMemo(() => tasks.filter(isLiveTask).length, [tasks]);

  const handleAssignClose = () => {
    setAssignOpen(false);
    // The list refreshes itself via the ["tasks"] invalidation; the detail payload's
    // openTaskCount needs its own refetch, which the parent wires through this callback.
    onTasksChanged?.();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-app-text-secondary m-0">
          {isFetching
            ? "Loading tasks…"
            : tasks.length === 0
            ? "No tasks linked to this record yet."
            : `${openCount} open · ${tasks.length} total`}
        </p>
        {canAssignTask && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AssignmentIcon fontSize="small" />}
            onClick={() => setAssignOpen(true)}
          >
            Assign task
          </Button>
        )}
      </div>

      {isFetching && tasks.length === 0 ? (
        <div className="flex justify-center py-8">
          <CircularProgress size={28} />
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <ModuleTaskCard key={task.id} task={task} onStatusChanged={onTasksChanged} />
          ))}
        </div>
      )}

      {assignOpen && (
        <TaskModal
          open={assignOpen}
          modalData={{ clientId, name: clientName, specificTask: assignTitle }}
          handleClose={handleAssignClose}
          module={taskModule}
          moduleEntryType={entryType}
          moduleEntryId={entryId}
        />
      )}
    </div>
  );
};

export default ModuleTasksTab;
